import { Flex, Text } from '@chakra-ui/react'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { isQueueAgentStatus, QueueAgentStatus } from '~/hooks/useCallQueue'
import { QueueControlButtonStyleValues } from '~/pages/Home/TopNav/componentStyles/QueueControlButton.style'
import { AgentCustomStatus } from '~/types/AgentCustomStatus'

import { type MenuCloseReason, MenuPlacement } from './Menu'
import { Menu } from './Menu'
import { MenuItem, MenuListItem } from './MenuItem'

export type QueueStatusMenuValue = QueueAgentStatus | string

type QueueStatusMenuProps = {
  selectedValue: QueueStatusMenuValue
  customStatuses: AgentCustomStatus[]
  handleSelect: (value: QueueStatusMenuValue) => string | void
  anchorRef?: React.RefObject<HTMLElement>
  /** Enables the keyboard-operable ARIA menu; called on Escape or Tab. */
  onKeyboardClose?: (reason: MenuCloseReason) => void
  labelledBy?: string
}

const QueueStatusMenu = ({
  selectedValue,
  customStatuses,
  handleSelect,
  anchorRef,
  onKeyboardClose,
  labelledBy,
}: QueueStatusMenuProps) => {
  const { t } = useTranslation()
  const menuItemData: MenuItem<QueueStatusMenuValue | null>[] = useMemo(() => {
    const items: MenuItem<QueueStatusMenuValue | null>[] = []

    if (selectedValue !== QueueAgentStatus.InQueue) {
      items.push({
        value: QueueAgentStatus.InQueue,
        content: <MenuListItem primaryText={t('queueStatusMenu.joinQueue')} />,
      })
    }

    if (
      selectedValue === QueueAgentStatus.InQueue ||
      !isQueueAgentStatus(selectedValue)
    ) {
      items.push({
        value: QueueAgentStatus.Offline,
        content: <MenuListItem primaryText={t('queueStatusMenu.goOffline')} />,
      })
    }

    const onlineStatuses = (customStatuses ?? []).filter(
      (status) =>
        status.availability === 'Online' && status.id !== selectedValue,
    )
    const awayStatuses = (customStatuses ?? []).filter(
      (status) => status.availability === 'Away' && status.id !== selectedValue,
    )

    const showOnlineSection = onlineStatuses.length > 0
    const showAwaySection = awayStatuses.length > 0

    const onlineItems = showOnlineSection
      ? [
          {
            value: null,
            content: (
              <Flex
                w="full"
                padding={'0rem 0.75rem'}
                alignItems={'center'}
                alignSelf={'stretch'}
                gap={'0.5rem'}
                borderRadius={'0.25rem'}
                background={'bg.emphasized'}
              >
                <Text
                  overflow={'hidden'}
                  color={'fg.default'}
                  textOverflow={'ellipsis'}
                  fontSize={'1rem'}
                  fontWeight={'700'}
                  lineHeight={'1.5rem'}
                >
                  {t('queueStatusMenu.online')}
                </Text>
              </Flex>
            ),
            clickDisabled: true,
          },
          ...onlineStatuses.map((status) => ({
            value: status.id,
            content: <MenuListItem primaryText={status.name} />,
          })),
        ]
      : []

    const awayItems = showAwaySection
      ? [
          {
            value: null,
            content: (
              <Flex
                w="full"
                padding={'0rem 0.75rem'}
                alignItems={'center'}
                alignSelf={'stretch'}
                gap={'0.5rem'}
                borderRadius={'0.25rem'}
                background={'bg.emphasized'}
              >
                <Text
                  overflow={'hidden'}
                  color={'fg.default'}
                  textOverflow={'ellipsis'}
                  fontSize={'1rem'}
                  fontWeight={'700'}
                  lineHeight={'1.5rem'}
                >
                  {t('queueStatusMenu.away')}
                </Text>
              </Flex>
            ),
            clickDisabled: true,
          },
          ...awayStatuses.map((status) => ({
            value: status.id,
            content: <MenuListItem primaryText={status.name} />,
          })),
        ]
      : []

    return [...items, ...onlineItems, ...awayItems]
  }, [customStatuses, selectedValue, t])

  return (
    <Menu
      anchorRef={anchorRef}
      items={menuItemData}
      onSelect={(value) => {
        if (value) handleSelect(value)
      }}
      options={{
        width: QueueControlButtonStyleValues.menuWidth,
        placement: MenuPlacement.BottomLeft,
      }}
      keyboard={
        onKeyboardClose && {
          onClose: onKeyboardClose,
          initialFocusIndex: 0,
          labelledBy,
        }
      }
    />
  )
}

export default QueueStatusMenu
